import type { JSX } from 'react';

export interface GenerationCredentialContext {
  workspaceId: string | null;
  credentials: { id: string; label: string }[];
}

/** IDs stay executable; their public labels are a presentation concern, never a resolver key. */
export function GenerationCaseText({
  text = '',
  context,
}: {
  text?: string;
  context?: GenerationCredentialContext;
}): JSX.Element {
  const tokens =
    /(\{\{\s*(?:[A-Za-z0-9_.-]|\\_)+\s*(?:\|[^}]*)?\}\}|[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/gi;
  return (
    <>
      {text.split(tokens).map((part, index) => {
        if (part.startsWith('{{'))
          return (
            <mark
              key={index}
              className="rounded bg-info-500/15 px-1 text-info-600"
              title={
                part === '{{BASE_URL}}'
                  ? 'Base URL from the environment selected when running. Choose an environment before launch.'
                  : 'Project variable. You can override its value when launching a run.'
              }
            >
              {part}
            </mark>
          );
        const credential = context?.credentials.find(
          (item) => item.id === part,
        );
        if (credential && context?.workspaceId)
          return (
            <a
              key={index}
              href={`/workspaces/${encodeURIComponent(context.workspaceId)}/manage?tab=credentials`}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded bg-violet-500/15 px-1 text-violet-700 underline decoration-violet-400/50 underline-offset-2 dark:text-violet-300"
              title={`Credential: ${credential.label}. Opens workspace Credentials in a new tab.`}
            >
              {credential.label}
            </a>
          );
        return part;
      })}
    </>
  );
}

/** Render native list boundaries as text; source markup never enters the live document. */
export function generationPreconditionItems(raw: string): string[] {
  const clean = (value: string) => {
    const text = value
      .replace(/<\/?(?:p|div|ul|ol|li)\s*>|<br\s*\/?>/gi, ' ')
      .replace(/<\/?(?:strong|b|em|i|u|s|code|span)\s*>/gi, '');
    return text
      .replace(
        /&(#x[0-9a-f]+|#[0-9]+|amp|lt|gt|quot|apos|nbsp);/gi,
        (entity, name: string) => {
          const known: Record<string, string> = {
            amp: '&',
            lt: '<',
            gt: '>',
            quot: '"',
            apos: "'",
            nbsp: ' ',
          };
          if (!name.startsWith('#')) return known[name.toLowerCase()] ?? entity;
          const point = Number.parseInt(
            name.slice(name[1]?.toLowerCase() === 'x' ? 2 : 1),
            name[1]?.toLowerCase() === 'x' ? 16 : 10,
          );
          return point >= 0 && point <= 0x10ffff
            ? String.fromCodePoint(point)
            : entity;
        },
      )
      .trim();
  };
  if (!/<li\s*>[\s\S]*?<\/li\s*>/i.test(raw))
    return [raw.trim()].filter(Boolean);
  return raw
    .split(/<\/?li\s*>/gi)
    .map(clean)
    .filter(Boolean);
}

export function GenerationPreconditions({
  text,
  context,
}: {
  text: string;
  context?: GenerationCredentialContext;
}): JSX.Element {
  const items = generationPreconditionItems(text);
  return (
    <div className="space-y-1">
      <p className="font-medium">Preconditions</p>
      <ol className="list-decimal space-y-1 pl-4">
        {items.map((item, index) => (
          <li key={index} className="whitespace-pre-wrap">
            <GenerationCaseText text={item} context={context} />
          </li>
        ))}
      </ol>
    </div>
  );
}
